import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { apiTokensApi, type ApiAccess, type ApiTokenInfo, type ApiTokenScope } from '../api/apiAccess';
import { confirmDialog } from '../shell/dialogs';
import { MfaStepUpModal } from '../shell/MfaStepUpModal';

const EXPIRY_OPTIONS = [
  { days: 30, label: '30 dní' },
  { days: 90, label: '90 dní' },
  { days: 180, label: '180 dní' },
  { days: 365, label: '1 rok' },
];

const ERROR_TEXT: Record<string, string> = {
  API_NOT_ENABLED: 'API přístup nemáš povolený. Požádej správce.',
  NAME_REQUIRED: 'Vyplň název tokenu (aspoň 2 znaky).',
  TOO_MANY_TOKENS: 'Máš už 5 aktivních tokenů. Nejdřív některý odvolej.',
  INVALID_EXPIRY: 'Vyber platnost tokenu.',
  TENANT_ADMIN_REQUIRED: 'Tuto akci smí jen Super Admin.',
};

function errorText(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return ERROR_TEXT[e.code] ?? `${fallback} (${e.code})`;
  return fallback;
}

function fmt(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function scopeLabel(scopes: ApiTokenScope[]): string {
  return scopes.includes('tasks:write') ? 'Úkoly: čtení a zápis' : 'Úkoly: jen čtení';
}

function stateLabel(t: ApiTokenInfo): string {
  if (t.revokedAt) return 'Odvolaný';
  if (!t.active) return 'Vypršel';
  return 'Aktivní';
}

function TokenTable({ tokens, onRevoke }: { tokens: ApiTokenInfo[]; onRevoke: (t: ApiTokenInfo) => void }) {
  if (tokens.length === 0) return <p className="text-muted small mb-0">Zatím žádný token.</p>;
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0">
        <thead>
          <tr><th>Název</th><th>Token</th><th>Oprávnění</th><th>Platí do</th><th>Naposledy použit</th><th>Stav</th><th /></tr>
        </thead>
        <tbody>
          {tokens.map((t) => (
            <tr key={t.id}>
              <td>{t.name}</td>
              <td><code>{t.prefix}…</code></td>
              <td className="small">{scopeLabel(t.scopes)}</td>
              <td className="small">{fmt(t.expiresAt)}</td>
              <td className="small">{t.lastUsedAt ? `${fmt(t.lastUsedAt)}${t.lastUsedIp ? ` · ${t.lastUsedIp}` : ''}` : '-'}</td>
              <td className="small">{stateLabel(t)}</td>
              <td className="text-end">
                {t.active && (
                  <button type="button" className="btn btn-sm btn-outline-danger" style={{ textTransform: 'none' }} onClick={() => onRevoke(t)}>
                    Odvolat
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ApiAccessPane() {
  const [data, setData] = useState<ApiAccess | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [allowWrite, setAllowWrite] = useState(false);
  const [days, setDays] = useState(90);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [stepUp, setStepUp] = useState(false);

  const load = useCallback(() => {
    apiTokensApi.listOwn().then(setData).catch((e) => setError(errorText(e, 'Načtení selhalo.')));
  }, []);
  useEffect(load, [load]);

  async function create() {
    setError(null);
    setBusy(true);
    try {
      const scopes: ApiTokenScope[] = allowWrite ? ['tasks:read', 'tasks:write'] : ['tasks:read'];
      const r = await apiTokensApi.createOwn({ name: name.trim(), scopes, expiresInDays: days });
      setCreated(r.token);
      setCopied(false);
      setName('');
      load();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'MFA_STEP_UP_REQUIRED') setStepUp(true);
      else setError(errorText(e, 'Vytvoření tokenu selhalo.'));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(t: ApiTokenInfo) {
    if (!(await confirmDialog({ title: 'Odvolat token?', message: `Token „${t.name}“ okamžitě přestane fungovat. Aplikace, které ho používají, ztratí přístup.`, confirmLabel: 'Odvolat', danger: true }))) return;
    try {
      await apiTokensApi.revokeOwn(t.id);
      load();
    } catch (e) {
      setError(errorText(e, 'Odvolání selhalo.'));
    }
  }

  async function copy() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="settings-form-layout settings-password-pane">
      {stepUp && (
        <MfaStepUpModal
          title="Nový API token - ověření"
          onClose={() => setStepUp(false)}
          onVerified={async () => { setStepUp(false); await create(); }}
        />
      )}
      <section className="settings-form-panel">
        <header className="settings-panel-head">
          <div>
            <span>Zabezpečení</span>
            <h2>API přístup</h2>
            <p>Osobní tokeny pro připojení aplikací k Úkolům. Token má stejná práva jako ty a nic navíc.</p>
          </div>
        </header>
        <div className="settings-form-body">
          {!data ? (
            <p className="settings-loading">{error ?? 'Načítám…'}</p>
          ) : !data.enabled ? (
            <div className="alert alert-warning mb-0">API přístup nemáš povolený. Povolit ho může Super Admin v Nastavení → Uživatelé.</div>
          ) : (
            <>
              {created && (
                <div className="alert alert-success">
                  <strong>Token je vytvořený. Zkopíruj si ho teď, znovu se už nezobrazí.</strong>
                  <div className="d-flex gap-2 mt-2 align-items-center">
                    <code style={{ wordBreak: 'break-all' }}>{created}</code>
                    <button type="button" className="btn btn-sm btn-outline-secondary" style={{ textTransform: 'none' }} onClick={() => void copy()}>
                      {copied ? 'Zkopírováno' : 'Kopírovat'}
                    </button>
                  </div>
                  <small className="d-block mt-2">Token posílej jen v hlavičce <code>Authorization: Bearer …</code>, nikdy v adrese. Neukládej ho do sdílených dokumentů.</small>
                  <button type="button" className="btn btn-sm btn-primary mt-2" onClick={() => setCreated(null)}>Mám ho uložený</button>
                </div>
              )}
              <div className="settings-field">
                <label className="form-label">Název tokenu</label>
                <input className="form-control" placeholder="např. Onevian na Macu" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="settings-field">
                <label className="form-label">Platnost</label>
                <select className="form-select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
                  {EXPIRY_OPTIONS.map((o) => <option key={o.days} value={o.days}>{o.label}</option>)}
                </select>
              </div>
              <div className="settings-field">
                <label className="d-flex align-items-center gap-2" style={{ textTransform: 'none' }}>
                  <input type="checkbox" checked={allowWrite} onChange={(e) => setAllowWrite(e.target.checked)} />
                  Povolit i zápis (zakládání a úpravy úkolů)
                </label>
                <small>Bez zaškrtnutí token Úkoly jen čte.</small>
              </div>
              {error && <div className="alert alert-danger">{error}</div>}
              <h3 className="h6 mt-4">Moje tokeny</h3>
              <TokenTable tokens={data.tokens} onRevoke={(t) => void revoke(t)} />
            </>
          )}
        </div>
        {data?.enabled && (
          <footer className="settings-form-footer">
            <button type="button" className="btn btn-primary" disabled={busy || name.trim().length < 2} onClick={() => void create()}>
              {busy ? 'Vytvářím…' : 'Vytvořit token'}
            </button>
          </footer>
        )}
      </section>
    </div>
  );
}

export function UserApiAccessSection({ userId }: { userId: number }) {
  const [data, setData] = useState<ApiAccess | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<boolean | null>(null);

  const load = useCallback(() => {
    apiTokensApi.adminGet(userId).then(setData).catch((e) => setError(errorText(e, 'Načtení selhalo.')));
  }, [userId]);
  useEffect(load, [load]);

  async function setEnabled(enabled: boolean) {
    if (!enabled && !(await confirmDialog({ title: 'Vypnout API přístup?', message: 'Všechny tokeny uživatele se okamžitě odvolají.', confirmLabel: 'Vypnout', danger: true }))) return;
    setError(null);
    setBusy(true);
    try {
      await apiTokensApi.adminSet(userId, enabled);
      load();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'MFA_STEP_UP_REQUIRED') setPending(enabled);
      else setError(errorText(e, 'Uložení selhalo.'));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(t: ApiTokenInfo) {
    if (!(await confirmDialog({ title: 'Odvolat token?', message: `Token „${t.name}“ okamžitě přestane fungovat.`, confirmLabel: 'Odvolat', danger: true }))) return;
    try {
      await apiTokensApi.adminRevoke(userId, t.id);
      load();
    } catch (e) {
      setError(errorText(e, 'Odvolání selhalo.'));
    }
  }

  return (
    <div className="border rounded p-3 mt-3">
      {pending !== null && (
        <MfaStepUpModal
          title="API přístup - ověření"
          onClose={() => setPending(null)}
          onVerified={async () => { const v = pending; setPending(null); await setEnabled(v); }}
        />
      )}
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <strong>API přístup</strong>
          <div className="small text-muted">Povolí uživateli vytvořit si osobní token pro Úkoly. Vypnutí odvolá všechny jeho tokeny.</div>
        </div>
        {data && (
          <div className="form-check form-switch m-0">
            <input className="form-check-input" type="checkbox" role="switch" checked={data.enabled} disabled={busy}
              onChange={(e) => void setEnabled(e.target.checked)} />
          </div>
        )}
      </div>
      {error && <div className="alert alert-danger mt-2 mb-0">{error}</div>}
      {data && data.tokens.length > 0 && (
        <div className="mt-3">
          <TokenTable tokens={data.tokens} onRevoke={(t) => void revoke(t)} />
        </div>
      )}
    </div>
  );
}
